import React, { useState, useEffect, useCallback } from 'react';
import { getConversations } from '../../../utils/db';
import api from '../../../utils/api';

function ChatHistory({ onSelectChat, currentUser, refreshKey }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      const userId = currentUser?.uid || 'mock-user-123';
      const chats = await getConversations(userId);
      setConversations(chats);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations, refreshKey]);

  const handleSelectChat = async (chat) => {
    onSelectChat(chat);
  };

  const handleInvite = async () => {
    try {
      const userId = currentUser?.uid || 'mock-user-123';
      const userName = currentUser?.displayName || 'Anonymous';

      // Create invite via backend API (which persists to Firestore)
      const result = await api.createInvite({
        createdBy: userId,
        createdByName: userName,
      });

      // Generate the invite URL using IP address instead of localhost
      const hostname = window.location.hostname === 'localhost'
        ? (process.env.REACT_APP_HOST || '192.168.0.102')
        : window.location.hostname;
      const port = window.location.port ? `:${window.location.port}` : '';
      const baseUrl = `${window.location.protocol}//${hostname}${port}`;
      const link = `${baseUrl}/invite/${result.invite_code}`;
      setInviteLink(link);
      setShowInviteModal(true);
      setCopied(false);
    } catch (err) {
      console.error('Failed to create invite:', err);
      alert('Failed to create invite link. Please try again.');
    }
  };

  const handleCopyLink = () => {
    // Create a temporary textarea element for reliable mobile copying
    const textarea = document.createElement('textarea');
    textarea.value = inviteLink;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, 99999); // For mobile

    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => {
        setShowInviteModal(false);
        setCopied(false);
      }, 1500);
    } catch {
      // Fallback: try Clipboard API
      navigator.clipboard.writeText(inviteLink).then(() => {
        setCopied(true);
        setTimeout(() => {
          setShowInviteModal(false);
          setCopied(false);
        }, 1500);
      }).catch(() => {
        // Last resort: select text in the input for manual copy
        const input = document.querySelector('.invite-link-input');
        if (input) {
          input.select();
          input.setSelectionRange(0, 99999);
        }
      });
    }

    document.body.removeChild(textarea);
  };

  if (loading) {
    return (
      <div className="chat-history-container">
        <div className="chat-history-header">
          <h1>Chats</h1>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-history-container">
      <div className="chat-history-header">
        <h1>Chats</h1>
        <p>
          {conversations.length > 0
            ? `You have had a conversation with ${conversations.length} person${conversations.length !== 1 ? 's' : ''}.`
            : 'No conversations yet. Invite someone to chat!'}
        </p>
      </div>

      <button className="new-chat-btn" onClick={handleInvite}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13"></path>
          <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
        </svg>
        Invite to Chat
      </button>

      <div className="chat-list">
        {conversations.map(chat => (
          <div key={chat.id} className="chat-item" onClick={() => handleSelectChat(chat)}>
            <img src={chat.avatar} alt={chat.name} className="chat-avatar" />
            <div className="chat-info">
              <h3 className="chat-name">
                {chat.name}
                {chat.unreadCount > 0 && (
                  <span style={{
                    marginLeft: 8,
                    background: '#ef4444',
                    color: '#fff',
                    borderRadius: 10,
                    padding: '1px 7px',
                    fontSize: 11,
                    fontWeight: 600,
                    verticalAlign: 'middle',
                  }}>
                    {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                  </span>
                )}
              </h3>
              <p className="chat-last-message">
                {chat.lastMessage || 'No messages yet'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Invite Link Modal */}
      {showInviteModal && (
        <div className="invite-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-modal" onClick={e => e.stopPropagation()}>
            <button className="invite-modal-close" onClick={() => setShowInviteModal(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div className="invite-modal-icon">🔗</div>
            <h2 className="invite-modal-title">Share this link</h2>
            <p className="invite-modal-subtitle">
              Share this invite link with someone to start a private chat.
              They'll be able to join instantly!
            </p>
            <div className="invite-link-container">
              <input
                className="invite-link-input"
                type="text"
                value={inviteLink}
                readOnly
                onClick={e => e.target.select()}
              />
              <button className="invite-copy-btn" onClick={handleCopyLink}>
                {copied ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatHistory;
